/**
 * Pure geometry helpers for Canvas frame auto-attach.
 *
 * Framework-free; no React, no Yjs, no React Flow. Tested in isolation.
 * Consumed by useFrameDropTarget, useAutoAttach, useFrameDelete.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Bounding-box hit-test using a half-open interval [x, x+w) × [y, y+h). */
export function pointInBox(point: Point, box: Box): boolean {
  return (
    point.x >= box.x &&
    point.x <  box.x + box.width &&
    point.y >= box.y &&
    point.y <  box.y + box.height
  );
}

/** AABB overlap test, half-open semantics — adjacent boxes do not overlap. */
export function boxOverlap(a: Box, b: Box): boolean {
  return (
    a.x < b.x + b.width  && a.x + a.width  > b.x &&
    a.y < b.y + b.height && a.y + a.height > b.y
  );
}

/** Convert an absolute (canvas-space) position to coordinates relative to a frame. */
export function absoluteToFrameLocal(absolute: Point, frame: Point): Point {
  return { x: absolute.x - frame.x, y: absolute.y - frame.y };
}

/** Convert frame-local coordinates back to absolute (canvas-space). */
export function frameLocalToAbsolute(frame: Point, local: Point): Point {
  return { x: frame.x + local.x, y: frame.y + local.y };
}

/** Minimal node shape this module needs — the engine emits richer nodes; we narrow. */
export interface SpatialNode {
  id: string;
  type: string;
  position: Point;
  size: { width: number; height: number } | null;
  parentId: string | null;
}

/**
 * Given an absolute point and a list of nodes (with positions already
 * flattened to absolute), return the id of the innermost frame under the
 * point, or null if none.
 *
 * Caller must pass nodes with absolute positions — the auto-attach hook
 * computes those by walking parentId chains before invoking this. exclude
 * carries the id(s) of the node(s) currently being dragged so a frame
 * cannot drop onto itself or another frame in the same drag selection.
 *
 * Performance note: parent-chain walk is O(depth × N) in the worst case
 * (Array.find per ancestor lookup). Acceptable at v1 with ≤ 50 children
 * per frame and shallow nesting. If deeper hierarchies ship, switch the
 * `nodes` parameter to a Map<id, node> in the call site to drop to O(depth).
 */
export function findFrameUnderPoint(
  point: Point,
  nodes: readonly SpatialNode[],
  exclude: readonly string[] = [],
): string | null {
  const excluded = new Set(exclude);

  // Build innermost-first ordering: frames whose parentId points at another
  // frame come first. Reusing the input order is a stable heuristic when
  // depth is unknown; the test pins both cases.
  const candidates = nodes.filter(
    n => n.type === 'frame' && n.size !== null && !excluded.has(n.id),
  );

  // Score each candidate by depth (parentId chain length); break ties by
  // (smaller area = more specific).
  let best: { id: string; depth: number; area: number } | null = null;
  for (const c of candidates) {
    const box: Box = { x: c.position.x, y: c.position.y, width: c.size!.width, height: c.size!.height };
    if (!pointInBox(point, box)) continue;

    let depth = 0;
    let cur: SpatialNode | undefined = c;
    while (cur && cur.parentId) {
      depth += 1;
      const parentId: string = cur.parentId;
      cur = nodes.find(n => n.id === parentId);
    }
    const area = box.width * box.height;

    if (
      best === null ||
      depth > best.depth ||
      (depth === best.depth && area < best.area)
    ) {
      best = { id: c.id, depth, area };
    }
  }

  return best?.id ?? null;
}
